import { isNil } from "./helpers";

interface ParseQueryParamsObjectOptions {
  omitQuestionMark?: boolean;
}

/**
 * Parses an object of query parameters and constructs a query string with question mark at the start of the string.
 *
 * @param queryParams An object representing query parameters. The object's keys are the parameter names,
 * and the values are the parameter values. The values can be strings or arrays of strings. If value is an
 * array the query parameter will be repeated. Repeated query parameters are checked for duplicates.
 *
 * @param options Optional settings for parsing the query parameters.
 * @param options.omitQuestionMark If true, omits the leading question mark in the query string.
 *
 */
export const parseQueryParamsObject = (queryParams: object | undefined, options?: ParseQueryParamsObjectOptions): string => {
  let queryParamsResult = "";

  if (queryParams) {
    const queryParamsKeys = Object.keys(queryParams);

    if (queryParamsKeys.length > 0) {
      /** Incremented once any query param is added */
      let nonUndefinedUniqueQueryParamsCount = 0;

      queryParamsKeys.forEach((key) => {
        const value = (queryParams as any)[key];

        if (!isNil(value)) {
          // If param value is array repeat the query param
          if (Array.isArray(value)) {
            if (value.length > 0) {
              /** Incremented once query param from current array is added */
              let arrayValueNonUndefinedQueryParamsCount = 0;

              value.forEach((arrayValue) => {
                if (arrayValue) {
                  const queryParam = `${key}=${encodeURIComponent(String(arrayValue))}`;

                  // Prevent adding duplicate query params
                  if (!isQueryDuplicate(queryParamsResult, key, queryParam, options)) {
                    if (
                      !options?.omitQuestionMark &&
                      nonUndefinedUniqueQueryParamsCount === 0 &&
                      arrayValueNonUndefinedQueryParamsCount === 0
                    ) {
                      queryParamsResult = "?";
                    }

                    if (arrayValueNonUndefinedQueryParamsCount > 0 || nonUndefinedUniqueQueryParamsCount > 0) {
                      queryParamsResult += "&";
                    }

                    queryParamsResult += queryParam;

                    // Cannot use the loop iterator because query param might be `undefined`
                    nonUndefinedUniqueQueryParamsCount++;
                    arrayValueNonUndefinedQueryParamsCount++;
                  }
                }
              });
            }
          } else {
            if (!options?.omitQuestionMark && nonUndefinedUniqueQueryParamsCount === 0) {
              queryParamsResult = "?";
            }

            if (nonUndefinedUniqueQueryParamsCount > 0) {
              queryParamsResult += "&";
            }

            queryParamsResult += `${key}=${encodeURIComponent(String(value))}`;

            nonUndefinedUniqueQueryParamsCount++;
          }
        }
      });
    }
  }

  return queryParamsResult;
};

const MOCK_URL = "http://x.x";

const isQueryDuplicate = (
  queryParamsPath: string,
  queryParamKey: string,
  queryParamValue: string,
  options?: ParseQueryParamsObjectOptions
) => {
  if (queryParamsPath === "") {
    return false;
  }

  const url = new URL(MOCK_URL + (options?.omitQuestionMark ? `?${queryParamsPath}` : queryParamsPath));
  return url.searchParams.getAll(queryParamKey).includes(queryParamValue);
};
